/**
 * RAG API Service
 *
 * Client for the /api/v1/rag/* endpoints on Agentserve
 */

import { config } from '../../config'
import type { AdaptiveChartGranularity, MetricsDateRange } from '../../types/metricsDateRange'
import { adaptiveGranularityForMetricsRange, metricsDateRangeToRagApiParams } from '../../types/metricsDateRange'
import { ApiError } from './core'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const agentserveServiceFetch = createServiceFetch(config.api.agentserveUrl)

// ============================================================================
// Helper for Agentserve requests (similar to agentserveApi pattern)
// ============================================================================

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem(config.auth.tokenKey)
  const tenantId = localStorage.getItem('tagp-tenant-id')
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-ID': tenantId }),
  }
}

async function agentserveRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${config.api.agentserveUrl}${endpoint}`

  const response = await agentserveServiceFetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData?.detail || errorData?.error || 'Request failed',
      response.status,
      errorData
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

function appendRagDateRangeSearchParams(params: URLSearchParams, dateRange: MetricsDateRange) {
  const p = metricsDateRangeToRagApiParams(dateRange)
  if (p.timeRange !== 'custom') params.set('time_range', p.timeRange)
  if (p.startDate && p.endDate) {
    params.set('start_date', p.startDate)
    params.set('end_date', p.endDate)
  }
}

const RAG_TREND_TRUNC: Record<AdaptiveChartGranularity, string> = {
  hourly: 'hour',
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
}

/** Adaptive SQL date_trunc unit for `/analytics/trend` (matches dashboard rules). */
export function ragTrendTruncFromDateRange(range: MetricsDateRange): string {
  return RAG_TREND_TRUNC[adaptiveGranularityForMetricsRange(range)]
}

// ============================================================================
// Types
// ============================================================================

export interface QueryAnalysisRequest {
  query: string
  context?: string
}

export interface QueryAnalysisResponse {
  query: string
  complexity: 'simple' | 'moderate' | 'complex'
  intent?: string
  recommended_strategy: string
  confidence: number
  reasoning: string
  suggested_enhancements: string[]
}

export interface RetrieveRequest {
  query: string
  project_id?: string
  table_ids?: string[]
  connector_ids?: string[]
  strategy?: 'auto' | 'direct' | 'hyde' | 'multi_query' | 'decomposition'
  top_k?: number
  enable_hyde?: boolean
  enable_multi_query?: boolean
  enable_decomposition?: boolean
  enable_reranking?: boolean
  enable_correction?: boolean
  include_citations?: boolean
  verify_citations?: boolean
}

export interface RetrievalResultItem {
  content: string
  score: number
  source?: string
  source_type?: string
  metadata: Record<string, unknown>
}

export interface QualityMetrics {
  overall_quality: number
  relevance_score: number
  coverage_score: number
  missing_aspects: string[]
  needs_correction: boolean
  suggested_expansion?: string
}

export interface Citation {
  index: number
  source_id: string
  source_name: string
  source_type: string
  content_snippet: string
  confidence: number
  page_or_location?: string
  url?: string
}

export interface CitedResponse {
  text: string
  citations: Citation[]
  citation_coverage: number
  uncited_claims: string[]
  verification_passed: boolean
  total_claims: number
  cited_claims: number
}

export interface RetrieveResponse {
  query: string
  results: RetrievalResultItem[]
  total_candidates: number
  quality?: QualityMetrics
  was_corrected: boolean
  correction_attempts: number
  cited_response?: CitedResponse
  latency_ms: number
  strategies_used: string[]
}

export interface StrategyInfo {
  id: string
  name: string
  description: string
  category: string
  requires_llm: boolean
  avg_latency_ms?: number
  effectiveness_score?: number
}

export interface StrategyStats {
  strategy: string
  query_complexity?: string
  usage_count: number
  avg_quality: number
  avg_latency_ms: number
  success_rate: number
  correction_rate: number
  window_start?: string
  window_end?: string
}

export interface QualityTrendPoint {
  timestamp: string
  avg_quality: number
  avg_latency: number
  count: number
}

export interface RecentRetrieval {
  id: string
  query_hash: string
  query_text?: string
  query_complexity?: string
  strategies_used: string[]
  overall_quality: number
  total_time_ms: number
  was_corrected: boolean
  correction_attempts: number
  created_at: string
}

export interface CostSummary {
  embedding_tokens: number
  llm_tokens: number
  estimated_cost: number
  retrieval_count: number
}

// New analytics types
export interface AnalyticsOverview {
  total_retrievals: number
  unique_queries: number
  avg_quality: number
  avg_latency_ms: number
  success_rate: number
  correction_rate: number
  total_embedding_tokens: number
  total_llm_tokens: number
  trends: {
    retrievals: number
    quality: number
    latency: number
  }
}

export interface ComplexityDistribution {
  complexity: string
  count: number
  avg_quality: number
  avg_latency: number
}

export interface LatencyDistribution {
  percentiles: {
    p50: number
    p90: number
    p95: number
    p99: number
    min: number
    max: number
  }
  buckets: Array<{
    range: string
    count: number
  }>
}

export interface QualityDistribution {
  bucket: string
  count: number
  correction_rate: number
}

export interface TopQuery {
  query_hash: string
  query_text?: string
  count: number
  avg_quality: number
  avg_latency: number
  correction_rate: number
}

/**
 * Consolidated analytics response containing all dashboard data
 */
export interface AllAnalytics {
  time_range: string
  trend_granularity?: string
  start_date?: string | null
  end_date?: string | null
  environment?: string
  overview: AnalyticsOverview
  cost: CostSummary
  trend: { data: QualityTrendPoint[] }
  quality_distribution: { distribution: QualityDistribution[] }
  complexity_distribution: { distribution: ComplexityDistribution[] }
  latency_distribution: LatencyDistribution
  recent_retrievals: { retrievals: RecentRetrieval[]; count: number }
  effectiveness: { strategies: StrategyStats[] }
  top_queries: { queries: TopQuery[] }
}

// ============================================================================
// API Functions
// ============================================================================

const RAG_BASE = '/api/v1/rag'

/**
 * Analyze a query to determine complexity and recommended strategy
 */
export async function analyzeQuery(request: QueryAnalysisRequest): Promise<QueryAnalysisResponse> {
  return agentserveRequest<QueryAnalysisResponse>(`${RAG_BASE}/analyze`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

/**
 * Execute RAG retrieval with specified configuration
 */
export async function retrieve(request: RetrieveRequest): Promise<RetrieveResponse> {
  return agentserveRequest<RetrieveResponse>(`${RAG_BASE}/retrieve`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

/**
 * Assess quality of retrieved documents
 */
export async function assessQuality(
  query: string,
  documents: Record<string, unknown>[]
): Promise<QualityMetrics> {
  return agentserveRequest<QualityMetrics>(`${RAG_BASE}/assess-quality`, {
    method: 'POST',
    body: JSON.stringify({ query, documents }),
  })
}

/**
 * List available RAG strategies
 */
export async function listStrategies(): Promise<StrategyInfo[]> {
  return agentserveRequest<StrategyInfo[]>(`${RAG_BASE}/strategies`)
}

/**
 * Get strategy effectiveness statistics
 */
export async function getStrategyEffectiveness(
  dateRange: MetricsDateRange,
  complexity?: string,
  environment?: string
): Promise<{ strategies: StrategyStats[] }> {
  const params = new URLSearchParams()
  appendRagDateRangeSearchParams(params, dateRange)
  if (complexity) params.append('complexity', complexity)
  if (environment) params.append('environment', environment)
  return agentserveRequest<{ strategies: StrategyStats[] }>(
    `${RAG_BASE}/analytics/effectiveness?${params}`
  )
}

/**
 * Get quality trend over time
 */
export async function getQualityTrend(
  dateRange: MetricsDateRange,
  environment?: string
): Promise<{ data: QualityTrendPoint[] }> {
  const params = new URLSearchParams({ granularity: ragTrendTruncFromDateRange(dateRange) })
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<{ data: QualityTrendPoint[] }>(`${RAG_BASE}/analytics/trend?${params}`)
}

/**
 * Get recent retrieval records
 */
export async function getRecentRetrievals(
  limit: number = 20,
  minQuality?: number,
  onlyCorrected: boolean = false,
  environment?: string,
  dateRange?: MetricsDateRange
): Promise<{ retrievals: RecentRetrieval[] }> {
  const params = new URLSearchParams({ limit: limit.toString() })
  if (minQuality !== undefined) params.append('min_quality', minQuality.toString())
  if (onlyCorrected) params.append('only_corrected', 'true')
  if (environment) params.append('environment', environment)
  if (dateRange) appendRagDateRangeSearchParams(params, dateRange)
  else params.set('time_range', '7d')
  return agentserveRequest<{ retrievals: RecentRetrieval[] }>(
    `${RAG_BASE}/analytics/recent?${params}`
  )
}

/**
 * Get cost summary for RAG operations
 */
export async function getCostSummary(
  dateRange: MetricsDateRange,
  environment?: string
): Promise<CostSummary> {
  const params = new URLSearchParams()
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<CostSummary>(`${RAG_BASE}/analytics/cost?${params}`)
}

/**
 * Get high-level analytics overview
 */
export async function getAnalyticsOverview(
  dateRange: MetricsDateRange,
  environment?: string
): Promise<AnalyticsOverview & { time_range: string }> {
  const params = new URLSearchParams()
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<AnalyticsOverview & { time_range: string }>(
    `${RAG_BASE}/analytics/overview?${params}`
  )
}

/**
 * Get complexity distribution
 */
export async function getComplexityDistribution(
  dateRange: MetricsDateRange,
  environment?: string
): Promise<{ distribution: ComplexityDistribution[] }> {
  const params = new URLSearchParams()
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<{ distribution: ComplexityDistribution[] }>(
    `${RAG_BASE}/analytics/complexity-distribution?${params}`
  )
}

/**
 * Get latency distribution with percentiles
 */
export async function getLatencyDistribution(
  dateRange: MetricsDateRange,
  environment?: string
): Promise<LatencyDistribution & { time_range: string }> {
  const params = new URLSearchParams()
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<LatencyDistribution & { time_range: string }>(
    `${RAG_BASE}/analytics/latency-distribution?${params}`
  )
}

/**
 * Get quality distribution
 */
export async function getQualityDistribution(
  dateRange: MetricsDateRange,
  environment?: string
): Promise<{ distribution: QualityDistribution[] }> {
  const params = new URLSearchParams()
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<{ distribution: QualityDistribution[] }>(
    `${RAG_BASE}/analytics/quality-distribution?${params}`
  )
}

/**
 * Get top queries by frequency or quality
 */
export async function getTopQueries(
  dateRange: MetricsDateRange,
  limit: number = 10,
  sortBy: 'count' | 'quality' = 'count',
  environment?: string
): Promise<{ queries: TopQuery[] }> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    sort_by: sortBy,
  })
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<{ queries: TopQuery[] }>(`${RAG_BASE}/analytics/top-queries?${params}`)
}

/**
 * Get all RAG analytics data in a single API call
 *
 * This consolidated endpoint fetches all dashboard metrics at once
 * to avoid rate limit issues from making multiple separate API calls.
 */
export async function getAllAnalytics(
  dateRange: MetricsDateRange,
  environment?: string
): Promise<AllAnalytics> {
  const params = new URLSearchParams()
  appendRagDateRangeSearchParams(params, dateRange)
  if (environment) params.append('environment', environment)
  return agentserveRequest<AllAnalytics>(`${RAG_BASE}/analytics/all?${params}`)
}

// Export all functions as ragApi object
export const ragApi = {
  analyzeQuery,
  retrieve,
  assessQuality,
  listStrategies,
  getStrategyEffectiveness,
  getQualityTrend,
  getRecentRetrievals,
  getCostSummary,
  getAnalyticsOverview,
  getComplexityDistribution,
  getLatencyDistribution,
  getQualityDistribution,
  getTopQueries,
  getAllAnalytics,
}
